import React from 'react'
import { Dialog } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ModalDialog } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'

export type Raci = {
  element: string
  roles: [{ stakeholder: string; role: string }]
}

type RaciModalProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  raci: Raci[]
}

const roleColors: Record<string, string> = {
  Responsible: 'bg-red-500 text-black',
  Accountable: 'bg-blue-500 text-black',
  Consulted: 'bg-yellow-400 text-black',
  Informed: 'bg-gray-300 text-black',
}

const RaciModal: React.FC<RaciModalProps> = ({ open, setOpen, raci }) => {
  const { t } = useTranslation()

  const stakeholders = Array.from(new Set(raci.flatMap((r) => r.roles.map((role) => role.stakeholder))))

  return (
    <ModalDialog isOpen={open} setOpen={setOpen} className='!max-w-7xl my-5 !w-fit'>
      <Dialog.Title as='h3' className='leading-6 text-lg flex justify-between'>
        {t('RACI Matrix')}
        <button onClick={() => setOpen(false)}>
          <XMarkIcon className='w-5 h-5 stroke-gray-500' />
        </button>
      </Dialog.Title>

      <div className='overflow-auto mt-4'>
        <table className='inline-table mx-auto table-auto border border-gray-300 rounded text-sm'>
          <thead>
            <tr className='bg-gray-100'>
              <th className='px-4 py-2 text-left border-b'>Task</th>
              {stakeholders.map((stakeholder) => (
                <th key={stakeholder} title={stakeholder} className='p-0.5 w-20 max-w-20 h-20 max-h-20 text-center'>
                  {stakeholder}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {raci.map(({ element, roles }) => (
              <tr key={element} className='border-t'>
                <td className='px-4 py-2 font-medium border-r whitespace-nowrap'>{element}</td>
                {stakeholders.map((stakeholder) => {
                  const roleObj = roles.find((r) => r.stakeholder === stakeholder)
                  const role = roleObj?.role
                  const short = role?.charAt(0).toUpperCase()
                  const color = role ? roleColors[role] : ''
                  return (
                    <td
                      key={stakeholder}
                      title={role || ''}
                      className={`px-2 py-2 text-center border w-20 max-w-20 h-20 max-h-20 truncate ${color} text-3xl`}
                    >
                      {short || ''}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModalDialog>
  )
}

export default RaciModal
